
<script setup>
import { computed } from "vue";
import MainLayout from "@/components/layout/MainLayout.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import ReportFilterBar from "@/components/reports/ReportFilterBar.vue";
import ReportStatCard from "@/components/reports/ReportStatCard.vue";
import RevenueTrendCard from "@/components/reports/RevenueTrendCard.vue";
import CollectionByChannelCard from "@/components/reports/CollectionByChannelCard.vue";
import ArrearsAgingReport from "@/components/reports/ArrearsAgingReport.vue";
import ReportHighlightsCard from "@/components/reports/ReportHighlightsCard.vue";
import { useReportStore } from "@/stores/report";

const reportStore = useReportStore();

const year = computed({
  get: () => reportStore.filters.year,
  set: (v) => reportStore.setYear(v),
});
const project = computed({
  get: () => reportStore.filters.project,
  set: (v) => reportStore.setProject(v),
});

const formatCurrency = (v) =>
  new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(v || 0);

// ✅ Export รายงานเป็น CSV จริง
const handleExportReport = () => {
  const trend = reportStore.revenueTrend;
  if (!trend.length) return;

  const rows = trend.map((item) => ({
    ปี: reportStore.filters.year,
    เดือน: item.month,
    "ยอดออกบิล (บาท)": item.invoiced,
    "ยอดจัดเก็บ (บาท)": item.collected,
    "อัตราจัดเก็บ (%)": item.invoiced
      ? Math.round((item.collected / item.invoiced) * 100)
      : 0,
    "ส่วนต่าง (บาท)": item.invoiced - item.collected,
  }));

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => `"${row[h]}"`).join(",")),
  ].join("\n");

  // ✅ \uFEFF = BOM สำหรับให้ Excel เปิดภาษาไทยได้ถูกต้อง
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `รายงาน-${reportStore.filters.year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
</script>
<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายงานภาพรวม"
        subtitle="ดูสรุปรายรับ การจัดเก็บค่าส่วนกลาง สถานะลูกหนี้ และ Insight สำคัญของโครงการ"
        button-text="Export"
        button-icon="bi-file-earmark-arrow-down"
        button-color="danger"
        @action="handleExportReport"
      />

      <ReportFilterBar
        :year="year"
        :project="project"
        :year-options="reportStore.years"
        :project-options="reportStore.projectOptions"
        @update:year="year = $event"
        @update:project="project = $event"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <ReportStatCard
            title="รายรับสะสม"
            :value="formatCurrency(reportStore.summary.totalRevenue)"
            subtitle="ยอดรับชำระสะสมของปีที่เลือก"
            icon="bi-wallet2"
            color="success"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ReportStatCard
            title="ยอดออกบิลรวม"
            :value="formatCurrency(reportStore.summary.totalInvoiced)"
            subtitle="รวมยอดที่เรียกเก็บทั้งหมด"
            icon="bi-receipt-cutoff"
            color="primary"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ReportStatCard
            :value="`${reportStore.summary.collectionRate}%`"
            title="อัตราการจัดเก็บ"
            subtitle="เปรียบเทียบยอดรับชำระ/ออกบิล"
            icon="bi-graph-up-arrow"
            color="info"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ReportStatCard
            title="ยอดค้างชำระ"
            :value="formatCurrency(reportStore.summary.overdueAmount)"
            subtitle="ยอดค้างคงเหลือของปีที่เลือก"
            icon="bi-exclamation-circle"
            color="danger"
          />
        </div>
      </div>

      <div class="row g-4 mb-4">
        <div class="col-xl-7">
          <RevenueTrendCard :items="reportStore.revenueTrend" />
        </div>
        <div class="col-xl-5">
          <CollectionByChannelCard :items="reportStore.collectionByChannel" />
        </div>
      </div>

      <div class="row g-4 mb-4">
        <div class="col-xl-6">
          <ArrearsAgingReport :items="reportStore.arrearsAging" />
        </div>
        <div class="col-xl-6">
          <ReportHighlightsCard :items="reportStore.highlights" />
        </div>
      </div>

      <div class="row g-4">
        <div class="col-12">
          <div
            class="card border-0 shadow-sm rounded-4 bg-primary bg-opacity-10"
          >
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
              >
                <div>
                  <div class="fw-bold fs-5 mb-1">
                    ช่องว่างระหว่างยอดออกบิลและยอดรับชำระ
                  </div>
                  <div class="text-muted">
                    ใช้ประกอบการวางแผนติดตามลูกหนี้และการบริหารกระแสเงินสด
                  </div>
                </div>
                <div class="text-lg-end">
                  <div class="display-6 fw-bold text-primary mb-0">
                    {{ formatCurrency(reportStore.collectionGap) }}
                  </div>
                  <div class="text-muted small">Collection Gap</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

